<script lang="ts" setup>
import { computed, nextTick, onMounted, provide, ref, set, watch } from 'vue'
import { useInterval, useLocalStorage, useScroll, useTitle, watchThrottled } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router/composables'
import { $formatErrorMessage, $loadTextFromCDN } from '@/helpers'
import { clamp } from '@/utils'
import { api } from '@/api'
import { CHAPTER_LOCK, CHARGE_TYPE, ERROR_PAGE_LEVEL, STORAGE_CUSTOM_THEME, VIP_TYPE } from '@/constants'
import { useReadStore, useToastStore, useUserStore } from '@/stores'
import { useGotoBack } from '@/composables'
import { useChapterReadComplete } from '@/composables/tracks/useChapterReadComplete'
import { bannerShow, useBannerShow, useCallRocnovelApp } from '@/composables/useBannerShow'
import BasicCoinsSuccess from '@/components/BasicCoinsSuccess.vue'
import { isInThirdAppBrowser } from '@/utils/bowserEnv'

const props = defineProps<{
  contentId: string
  chapterId: string
}>()

const route = useRoute()
const router = useRouter()
const { $toast } = useToastStore()
const gotoBack = useGotoBack()
const readStore = useReadStore()
const userStore = useUserStore()
const { readCompleteTrack, resetReadComplete, chapterVisitTrack } = useChapterReadComplete()

const skeleton = ref(true)
const title = useTitle('Loading...')
const popupRecharge = ref(false)
const showRecommend = ref(false)
const coinsSuccess = ref(false)
const book: any = ref<GetContentGetcontentdetailResponse['data']>()
const discountPayInfo = ref<Record<string, any>>({
  discountOnceCoins: 0,
  bookImageUrl: '',
})
const showSaveUrl = ref(false)
const showGuidePopup = ref(false)

onMounted(async () => {
  // # 如果检测到页面是从“往返缓存”中读取的，刷新页面
  window.addEventListener('pageshow', (e) => {
    if (e.persisted)
      window.location.reload()
  })

  // 只有从支付返回时, 显示推荐
  if (route.query.recommend && !route.query.payment_status)
    showRecommend.value = true

  if (route.query.isShowpopupRecharge === '1' && !route.query.payment_status)
    popupRecharge.value = true

  // 展示Save Url按钮
  if (route.query.payment_status === 'success' && isInThirdAppBrowser())
    showSaveUrl.value = true

  skeleton.value = true
  try {
    getLeadSentence()
    await fetchBook()
    await fetchChapterList()

    if (!route.query.skipPosition) {
      // Note: existed different chapterId, void duplicate request chapter
      const existed = await fetchLatestPosition()
      if (existed)
        return
    }
    loadChapter()
  }
  catch (error) {
    $toast($formatErrorMessage(error))
  }
  finally {
    skeleton.value = false
  }

  useBannerShow()
})

const containerRef = ref<HTMLElement>()
const chapterRef = ref<HTMLElement>()
provide('containerRef', containerRef)

const chapter: any = ref<PostReadPlayResponse['data'] | null>()
const content = ref('')
const paragraphs = computed(() => {
  if (!content.value)
    return []
  return content.value.split(/[\n]/).filter(Boolean)
})
const { reset: resetReadTime } = useInterval(1000, { controls: true })

/**
 * 获取书籍信息
 */
async function fetchBook() {
  const { data } = await api.get<any, GetContentGetcontentdetailResponse>('/content/getContentDetail', { params: { contentId: props.contentId } })
  book.value = data
  // 支付成功且类型是折扣充值则显示成功弹窗
  if (route.query.payment_status === 'success' && Number(route.query?.subscribeType) === VIP_TYPE.DISCOUNT && userStore.$state.landingPageId && userStore.$state.landingPageContentId === props.contentId) {
    coinsSuccess.value = true
    discountPayInfo.value.discountOnceCoins = route.query.discountOnceCoins
    discountPayInfo.value.bookImageUrl = data.contentCoverUrl
  }
}

/**
 * 获取章节目录
 */
const chapterList = ref<any>([])
async function fetchChapterList() {
  try {
    const { data: chapterListData } = await api.get<any>('/content/getContentChapterBookList', {
      params: {
        contentId: props.contentId,
        pageIndex: 1,
        pageSize: 1500,
      },
    })
    chapterList.value = chapterListData.records
  }
  catch (error) {
    onErrorHandle()
  }
}

/**
 * 获取章节信息
 */
async function fetchChapter() {
  try {
    const { data } = await api.post<any, PostReadPlayResponse>('/read/play', {
      contentId: props.contentId,
      contentChapterId: props.chapterId,
      sequenceNo: 1,
    })
    const chapterItem = chapterList.value.find((item: any) => item.id === props.chapterId)
    if (chapterItem)
      chapterItem.lockStatus = CHAPTER_LOCK.UNLOCK

    chapter.value.lockStatus = CHAPTER_LOCK.UNLOCK
    chapter.value.sequenceNo = data.sequenceNo
    content.value = await $loadTextFromCDN(data.bookChapterFileUrl)
    const scrollY = Number(route.query.scrollY ?? 0)
    title.value = chapter.value?.contentChapterName
    const { y } = useScroll(containerRef)
    nextTick(() => {
      y.value = scrollY
    })
    nextTick(() => {
      reportScrollLocation(0)
      chapterVisitTrack(props.contentId, props.chapterId, chapterItem.sequenceNo)
    })
    // Note: for back last read chapter
    readStore.update({
      contentId: props.contentId,
      chapterId: chapter.value?.contentChapterId as string,
    })
    userStore.init({ force: true })
    await fetchChapterList()
  }
  catch (error: any) {
    const { code } = error
    // Note: 特殊业务处理, 403 为未购买章节
    if (code !== 403)
      onErrorHandle()
  }
  finally {
    resetReadTime()
    skeleton.value = false
  }
}

/**
 * 装载章节
 */
async function loadChapter() {
  // 获取章节信息
  const chapterItem = chapterList.value.find((item: any) => item.id === props.chapterId)
  if (!chapterItem)
    return

  useTitle(chapterItem.chapterName)
  chapter.value = {
    chargeType: book.value?.chargeType, // 单章付费 | 整本付费
    contentChapterName: chapterItem.chapterName,
    priceAmount: book.value?.chargeType === CHARGE_TYPE.CHAPTER ? chapterItem.chapterPrice : book.value?.priceAmount,
    memberCoin: userStore.$state.coinBalance,
    lockStatus: chapterItem.lockStatus,
    feeType: chapterItem.feeType,
    bookChapterFileUrl: chapterItem.bookChapterFileUrl,
    bookChapterFreeFileUrl: chapterItem.bookChapterFreeFileUrl,
    sequenceNo: chapterItem.sequenceNo,
    prev: chapterItem.preId ? { contentChapterId: chapterItem.preId } : null,
    next: chapterItem.nextId ? { contentChapterId: chapterItem.nextId } : null,
    chapterVersionId: chapterItem.chapterVersionId,
  }

  if (chapterItem.lockStatus !== CHAPTER_LOCK.LOCK) {
    try {
      chapterVisitTrack(props.contentId, props.chapterId, chapterItem.sequenceNo)
      content.value = await $loadTextFromCDN(chapter.value.bookChapterFileUrl)
      skeleton.value = false
    }
    catch (error) {
      onErrorHandle()
    }
  }
  else if (
    book.value?.chargeType === CHARGE_TYPE.CHAPTER
    && userStore.$state.autoLock
    && chapterItem.chapterPrice < userStore.$state.coinBalance
  ) {
    fetchChapter()
  }
  else if (
    book.value?.chargeType === CHARGE_TYPE.CONTENT
    && userStore.$state.autoLock
    && book.value?.priceAmount < userStore.$state.coinBalance
  ) {
    fetchChapter()
  }
  else if (chapterItem.chapterPrice > userStore.$state.coinBalance) {
    try {
      content.value = await $loadTextFromCDN(chapter.value.bookChapterFreeFileUrl)
      skeleton.value = false
    }
    catch (error) {
      onErrorHandle()
    }
  }
  else if (!userStore.$state.autoLock) {
    try {
      content.value = await $loadTextFromCDN(chapter.value.bookChapterFreeFileUrl)
      skeleton.value = false
    }
    catch (error) {
      onErrorHandle()
    }
  }
  const { y } = useScroll(containerRef)
  nextTick(() => {
    y.value = 0
  })
  nextTick(() => {
    reportScrollLocation(0)
  })
}

watch(() => props.contentId, () => {
  getLeadSentence()
  fetchBook()
})
watch(() => props.chapterId, () => {
  skeleton.value = true
  chapter.value = null
  content.value = ''
  loadChapter()
  resetReadComplete()
})

const landingList = ref<string[]>([])
async function getLeadSentence() {
  const { data } = await api.get<any, GetContentGetLeadSentenceResponse>(`/content/getLeadSentence?contentId=${props.contentId}`)
  if (!data)
    return
  landingList.value = data?.split('\n')?.filter(Boolean) || []
}
/**
 * 上一页
 */
function onPrevChapter() {
  if (!chapter.value!.prev)
    return

  router.replace({ name: 'chapter', params: { contentId: props.contentId, chapterId: chapter.value!.prev.contentChapterId } })
}

/**
 * 下一页
 */
function onNextChapter() {
  attributionTrack()
  if (!chapter.value!.next)
    return

  router.replace({ name: 'chapter', params: { contentId: props.contentId, chapterId: chapter.value!.next.contentChapterId } })
}

/**
 * 获取最后的阅读位置
 */
async function fetchLatestPosition() {
  const { data } = await api.post<any, PostReadPosResponse>('/read/pos', { contentId: book.value!.id })
  router.replace({
    name: 'chapter',
    params: { contentId: book.value!.id, chapterId: data.contentChapterId },
    query: {
      scrollY: String(data.sectionMediaPlayedSeconds),
      isShowpopupRecharge: route.query.isShowpopupRecharge,
      recommend: route.query.recommend,
    },
  })
  return props.chapterId !== data.contentChapterId
}

// Report scroll `y` position
async function reportScrollLocation(y: number = 0) {
  try {
    await api.post<any, PostReadReportResponse>('/read/report', {
      contentId: props.contentId,
      chapterId: props.chapterId,
      chapterVersionId: chapter.value?.chapterVersionId,
      sectionMediaPlayedSeconds: Math.floor(y),
      videoDuration: containerRef.value ? (containerRef.value.scrollHeight - containerRef.value.clientHeight) : 0,
    })
  }
  catch { } // TODO: Report Error
}

// Popup: TOC(Table of Contents)
const popupTOC = ref(false)
watch(() => route.fullPath, () => {
  popupTOC.value = false
})

/**
 * 打开目录
 */
function onOpenTOC() {
  popupTOC.value = true
  attributionTrack()
}

/**
 * 用户激活
 */
async function attributionTrack() {
  if (localStorage.getItem('IsActive'))
    return

  try {
    const res = await api.get<any, any>('/member/active')
    if (res.code === 0)
      localStorage.setItem('IsActive', '1')
    else
      $toast(res.msg || 'Error')
  }
  catch (error: any) {
    $toast(error.msg || 'Error')
  }
}

// 充值弹窗
watch(
  () => popupRecharge.value,
  (val) => {
    if (!val) {
      const newQuery = { ...route.query, isShowpopupRecharge: '0' }
      router.replace({ path: route.path, query: newQuery })
    }
  },
)

const autoLock = ref(true)
// 充值弹窗
const { y } = useScroll(containerRef)
watchThrottled(
  y,
  async () => {
    !popupRecharge.value && reportScrollLocation(y.value)

    handleReadComplete()
  },
  { throttle: 2000, leading: true },
)

function handleReadComplete() {
  const height = chapterRef.value?.offsetHeight || 0
  const clientHeight = containerRef.value?.clientHeight || 0
  const chapterItem = chapterList.value.find((item: any) => item.id === props.chapterId)
  const distance = height - y.value - clientHeight

  if (chapterItem && chapterItem.lockStatus !== CHAPTER_LOCK.LOCK && distance < 100)
    readCompleteTrack(props.contentId, props.chapterId, chapterItem.sequenceNo)
}

function onTOCCheckPass() {
  return true
}

// 解锁章节
async function onUnlockChapter() {
  if (!chapter.value)
    return

  if (chapter.value.priceAmount > userStore.$state.coinBalance) {
    popupRecharge.value = true
    const newQuery = { ...route.query, isShowpopupRecharge: '1' }
    router.replace({ path: route.path, query: newQuery })
  }

  if (autoLock.value !== userStore.$state.autoLock) {
    await api.post('/member/update/auto-lock', {
      autoLock: autoLock.value,
    })
    await userStore.init({ force: true })
  }
  fetchChapter()
}

function onErrorHandle() {
  router.replace({
    name: 'error',
    query: {
      level: ERROR_PAGE_LEVEL.DEFAULT,
      message: 'Network loading failure',
      redirect: window.encodeURIComponent(window.location.href),
    },
  })
}

function appendRecommendParams() {
  // 添加 recommend 参数
  const url = new URL(window.location.href)
  url.searchParams.set('recommend', '1')
  history.replaceState({}, '', url.href)
}

// 切换目录时, 去掉推荐
watch(
  () => route.query.recommend,
  (val) => {
    if (!val)
      showRecommend.value = false
  },
)

// Popup: Theme
// TODO: Move to constants/config?
const THEME_FONT_MAP = ['24', '26', '28', '30', '32', '34', '36', '38', '40', '42', '44', '46', '48', '50']
const THEME_BACKGROUNDS = ['#F6F7F9', '#D5E9D4', '#CEE7FF', '#FFE1DC', '#FBE6B5', '#292929']
const theme = useLocalStorage(STORAGE_CUSTOM_THEME, {
  background: '#F6F7F9',
  size: '38',
})
const THEME_FONT = computed(() => {
  if (!THEME_FONT_MAP.includes(theme.value.size))
    set(theme.value, 'size', '38')

  return {
    chapterTitle: `font-size: ${Number.parseInt(theme.value.size) / 2 + 2}px;font-weight: 500;`,
    chapterContent: `margin-top: 1em;font-size: ${Number.parseInt(theme.value.size) / 2}px;line-height: 1.7em;`,
    leadSentence: `font-size: ${Number.parseInt(theme.value.size) / 2 - 1}px;line-height: 1.7em;`,
    paragraphSpacing: 'margin-top: 1em;',
  }
})
provide('theme', theme)

const popupTheme = ref(false)
watch(() => route.fullPath, () => {
  popupTheme.value = false
})
function onToggleTheme() {
  popupTOC.value = false
  popupTheme.value = !popupTheme.value
}
function onChangeThemeSize(zoom: number) {
  const index = clamp(THEME_FONT_MAP.indexOf(theme.value.size) + zoom, 0, THEME_FONT_MAP.length - 1)
  set(theme.value, 'size', THEME_FONT_MAP[index])
}
function handleCoinsSuccess() {
  coinsSuccess.value = false
  // 删除subscribeType和discountOnceCoins
  const url = new URL(window.location.href)
  url.searchParams.delete('subscribeType')
  url.searchParams.delete('discountOnceCoins')
  history.replaceState({}, '', url.href)
}

// 拉起app
const showDownloadApp = computed(() => {
  return bannerShow.value && chapter.value && chapter.value.sequenceNo
})
const { callApp } = useCallRocnovelApp()
</script>

<template>
  <div v-if="skeleton" class="min-h-screen" :style="`background: ${theme.background}`">
    <div class="pt-20vh flex justify-center">
      <BasicSpinner class="mx-auto" />
    </div>
    <p class="mt-4 text-center font-bold">
      Loading...
    </p>
  </div>
  <div v-else>
    <div ref="containerRef" :class="`h-screen overflow-scroll color-${theme.background.replace('#', '')}`" :style="`background: ${theme.background}`">
      <BizDownloadApp
        v-if="showDownloadApp"
        :id="contentId"
        position="fixed"
        class="fixed top-0 w-full"
        :index="chapter?.sequenceNo"
      />
      <div
        class="fixed w-full z-50"
        :class="{
          'top-60px': bannerShow,
          'top-0': !bannerShow,
        }"
        style="transform: translate(0)"
      >
        <BasicNavbar v-show="y === 0 || !popupTheme" scale :title="chapter?.contentChapterName" />
        <div
          v-if="popupTheme && y !== 0" class="w-full fixed top-0 flex items-center z-10"
          :style="`background: ${theme.background}`"
        >
          <div class="p-2 pl-4" @click="gotoBack()">
            <i-icon-park-outline-left class="w-6 h-6" />
          </div>
        </div>

        <div v-if="showSaveUrl" class="theme-save-btn absolute right-12px top-12px z-99 w-80px h-28px flex items-center justify-center text-12px font-medium rounded-14px" @click="showGuidePopup = true">
          Save Url
        </div>
      </div>

      <!-- Container -->
      <div
        v-if="chapter"
        ref="chapterRef"
        class="px-20px pb-10 min-h-screen"
        :class="{
          'pt-24': bannerShow,
          'pt-10': !bannerShow,
        }"
        @click="onToggleTheme"
      >
        <div v-if="Number(chapter?.sequenceNo) === 1 && landingList?.length" class="introduce relative  rounded-10px mb-20px mt-10px" :style="THEME_FONT.leadSentence">
          <span class="bg-icon absolute w-42px h-28px top-18px left-16px z-1" />
          <div class="pt-25px pl-20px pr-14px pb-15px box-border relative w-full h-full z-2">
            <div v-for="(item, index) in landingList" :key="index" class="break-all">
              {{ item }}
              <div v-if="index !== landingList.length - 1" class="divice-line my-15px" />
            </div>
          </div>
        </div>
        <p :style="THEME_FONT.chapterTitle" class="chapter-title font-800">
          {{ chapter?.contentChapterName }}
        </p>
        <div :style="THEME_FONT.chapterContent" class="chapter-content">
          <p v-for="(item, index) in paragraphs" :key="index" :style="THEME_FONT.paragraphSpacing">
            {{ item }}
          </p>
        </div>
        <div v-if="chapter?.lockStatus === CHAPTER_LOCK.LOCK" class="pt-40px chapter-payment text-center">
          <template v-if="!showRecommend">
            <h3 class="theme-h3 font-bold text-18px text-center text-#32353A">
              Unlock Now
            </h3>
            <p class="theme-desc mt-8px lh-24px text-14px text-#65686F text-center">
              Show your support to inspire the writer to come<br>upwith more fantastic stories
            </p>
            <a
              class="theme-btn inline-block mt-24px px-24px h-40px lh-40px text-15px text-white text-center bg-primary rounded-full whitespace-normal w-300px"
              @click.stop="onUnlockChapter"
            >
              <span v-if="chapter.chargeType === CHARGE_TYPE.CHAPTER">{{ chapter.priceAmount }} coins to read this episode</span>
              <span v-else>{{ chapter.priceAmount }} coins to read the whole book</span>
            </a>
            <a
              v-if="showDownloadApp"
              class="theme-sub-btn inline-block mt-12px px-24px h-40px lh-40px text-15px text-white text-center rounded-full whitespace-normal w-300px font-medium"
              @click="callApp(contentId, chapter.sequenceNo)"
            >
              Continue to Read This book in APP
            </a>
            <div class="theme-radio mt-10px flex justify-center items-center" @click.stop="autoLock = !autoLock">
              <div class="radio flex justify-center items-center w-14px h-14px inline-block border b-#9296A0 b-rd-14px cursor" :class="{ selected: autoLock }">
                <span class="inline-block w-6px h-6px bg-#9875FB b-rd-6px" />
              </div>
              <span class="ml-6px label text-14px text-#65686F">Auto unlock</span>
            </div>
            <p class="theme-balance mt-24px pb-18px lh-24px text-14px text-#9296A0 text-center">
              Coins Balance: {{ chapter.memberCoin }} Coins
            </p>
          </template>
          <div class="mask" />
        </div>
        <div class="mt-5 flex items-center justify-between">
          <span
            class="theme-btn-bg w-105px h-39px lh-39px text-center text-14px bg-#0000000A rounded"
            :class="{ 'text-#999': !chapter.prev }" @click.stop="onPrevChapter"
          >
            Prev
          </span>
          <span class="theme-btn-bg w-100px h-39px lh-39px text-center text-14px bg-#0000000A rounded" @click.stop="onOpenTOC">
            Catalogue
          </span>
          <span
            v-if="!showRecommend"
            class="theme-btn-bg w-105px h-39px lh-39px text-center text-14px bg-#0000000A rounded"
            :class="{ 'text-#999': !chapter.next }"
            @click.stop="onNextChapter"
          >
            Next
          </span>
          <span
            v-else
            class="theme-btn-bg w-105px h-39px lh-39px text-center text-14px bg-#0000000A rounded"
            :class="{ 'text-#999': !chapter.next }"
            @click.stop="onUnlockChapter"
          >
            Unlock
          </span>
        </div>
        <!-- recommend -->
        <BizBookRecommend
          v-if="showRecommend"
          class="mt-24px"
          :book-id="contentId"
          @read="appendRecommendParams"
          @seeAll="appendRecommendParams"
          @error="showRecommend = false"
        />
      </div>

      <!-- Popup: 设置主题 -->
      <BasicPopup v-model="popupTheme" :mask="false">
        <div class="w-full px-5 pt-25px py-10" :style="`background: ${theme.background}`">
          <div class="mb-5 flex space-x-25px items-center">
            <p class="flex-1 min-w-8 text-14px text-#999">
              Size
            </p>
            <div class="flex-1 flex items-center justify-between">
              <span
                class="w-90px h-35px lh-35px text-center text-14px bg-#0000000A b-rd-tl-18px b-rd-bl-18px"
                :class="{ 'text-#999': theme.size === '24' }" @click="onChangeThemeSize(-1)"
              >
                A-
              </span>
              <span class="flex justify-between items-center w-90px h-35px lh-35px min-w-5 text-15px text-center bg-#0000000A">
                <span class="text-#0000001A">|</span>
                <span>{{ theme.size }}</span>
                <span class="text-#0000001A">|</span>
              </span>
              <span
                class="w-90px h-35px lh-35px text-center text-14px bg-#0000000A b-rd-tr-18px b-rd-br-18px"
                :class="{ 'text-#999': theme.size === '50' }" @click="onChangeThemeSize(1)"
              >
                A+
              </span>
            </div>
          </div>
          <div class="flex space-x-25px items-center">
            <div class="flex flex-1 justify-center space-x-20px">
              <span
                v-for="item in THEME_BACKGROUNDS" :key="item" class="w-30px h-30px border rounded-full"
                :style="`background: ${item};`"
                :class="item === theme.background ? 'border-2 border-primary' : 'border-#ccc'"
                @click="theme.background = item"
              />
            </div>
          </div>
          <div class="mt-5 flex items-center justify-between">
            <span
              class="theme-btn-bg w-115px h-39px lh-39px text-center text-14px bg-#0000000A rounded"
              :class="{ 'text-#999': !chapter?.prev }" @click="onPrevChapter"
            >
              Prev</span>
            <span
              class="theme-btn-bg w-80px h-39px lh-39px text-center text-14px bg-#0000000A rounded"
              @click.stop="onOpenTOC"
            >
              Catalogue
            </span>
            <span
              class="theme-btn-bg w-115px h-39px lh-39px text-center text-14px bg-#0000000A rounded"
              @click="onNextChapter"
            >
              Next
            </span>
          </div>
        </div>
      </BasicPopup>
      <BizPopupBookTOC
        v-model="popupTOC"
        :book="book"
        :content-id="props.contentId"
        :chapter-id="props.chapterId"
        :check-pass-goto-chapter="(index) => onTOCCheckPass()"
      />

      <!-- Popup: 充值列表 -->
      <BizPayTemplate
        v-if="popupRecharge"
        v-model="popupRecharge"
        :content-id="props.contentId"
        :content-chapter-id="props.chapterId"
        :book-name="book?.contentName"
        :chapter-name="chapter?.contentChapterName"
        :serial-status="book?.finishStatus"
        @getPayUrlSuccess="appendRecommendParams"
      />
      <BasicCoinsSuccess v-model="coinsSuccess" :discount-pay-info="discountPayInfo" @close="handleCoinsSuccess" />
      <BizGuideOpen v-model="showGuidePopup" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.recharge-list {
  max-height: 70vh;
  overflow-y: auto;
}
.popupRecharge {
  background: #fff;
  background-size: 100% auto;
  background-repeat: no-repeat;
}

.slide-enter-active {
  transition: all 0.2s ease;
  transform: translateY(0%);
}

.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter,
.slide-leave-to {
  transform: translateY(-100%);
}

.add-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
}

.add-text {
  color: #666;
}

.add-btn {
  color: #ff9928;
  display: flex;
  justify-content: center;
  align-items: center;
}

.add-btn-text {
  margin-right: 6px;
  font-weight: bold;
}

.recharge-interception {
  background: url("@/assets/bg-recharge-interception.webp");
  background-size: 100% 100%;
  height: 30px;
  padding-left: 25px;
}

:deep(.countdown .bg-primary) {
  background: #FF6E0F66;
}

.radio {
  span {
    display: none;
  }
  &.selected {
    border-color: #9875FB;
    span {
      display: inline-block
    }
  }
}
.chapter-payment {
  position: relative;
  .mask {
    position: absolute;
    top: -126px;
    left: 0;
    width: 100%;
    height: 126px;
  }
}
.divice-line {
  width: 100%;
  height: 1px;
  background-size: 18px 1px;
  background-repeat: repeat-x;
}

//默认
.color-F6F7F9 {
  .mask {
    background-image: linear-gradient(180deg, rgba(246,247,249,0.00) 20%, #F6F7F9 100%);
  }
  .theme-sub-btn {
    background-color: #fff;
    color: #9875FB;
  }
  .theme-save-btn {
    background: #ECEDEF;
    color: #65686F;
  }
  .introduce {
    background-color: #EDE7FB;
    color: #9875FB;
    .bg-icon {
      background: url('@/assets/introduce/default.png') no-repeat center;
      background-size: contain;
    }
    .divice-line {
      background-image: linear-gradient(to right, #D8CBFD 0%, #D8CBFD 50%, transparent 50%);
    }

  }
  .chapter-content,.chapter-title {
    color: #65686F;
  }
}

//绿色
.color-D5E9D4 {
  .mask {
    background-image: linear-gradient(180deg, rgba(213,233,212,0.00) 20%, #D5E9D4 100%);
  }
  .theme-h3 {
    color: #4F5E4E;
  }
  .theme-desc {
    color: #4F5E4E;
  }
  .theme-btn {
    background-color: #577953;
  }
  .theme-radio {
    color: #4F5E4E;
    .radio.selected {
      border-color: #577953;
      span {
        background-color: #577953;
      }
    }
  }
  .theme-balance {
    color: #87A184;
  }
  .theme-sub-btn {
    background: #F7FFF6;
    color: #577953;
  }
  .theme-save-btn {
    background: #F7FFF6;
    color: #577953;
  }
  .introduce {
    background-color: #F7FFF6;
    color: #577953;
    .bg-icon {
      background: url('@/assets/introduce/green.png') no-repeat center;
      background-size: contain;
    }
    .divice-line {
      background-image: linear-gradient(to right, #D5E9D4 0%, #D5E9D4 50%, transparent 50%);

    }

  }
  .chapter-title, .chapter-content {
    color: #4F5E4E;
  }
}

//蓝色
.color-CEE7FF {
  .mask {
    background-image: linear-gradient(180deg, rgba(206,231,255,0.00) 20%, #CEE7FF 100%);
  }
  .theme-h3 {
    color: #52648A;
  }
  .theme-desc {
    color: #52648A;
  }
  .theme-btn {
    background-color: #607BB3;
  }
  .theme-radio {
    color: #52648A;
    .radio.selected {
      border-color: #607BB3;
      span {
        background-color: #607BB3;
      }
    }
  }
  .theme-balance {
    color: #8DA1CA;
  }
  .theme-sub-btn {
    background: #F5FAFF;
    color: #607BB3;
  }
  .theme-save-btn {
    background: #F5FAFF;
    color: #607BB3;
  }
  .introduce {
    background-color: #F5FAFF;
    color: #607BB3;
    .bg-icon {
      background: url('@/assets/introduce/blue.png') no-repeat center;
      background-size: contain;
    }
    .divice-line {
      background-image: linear-gradient(to right, #CCE2F6 0%, #CCE2F6 50%, transparent 50%);
    }

  }
  .chapter-title,.chapter-content{
    color: #52648A;
  }
}

// 粉色
.color-FFE1DC {
  .mask {
    background-image: linear-gradient(180deg, rgba(255,225,220,0.00) 20%, #FFE1DC 100%);
  }
  .theme-h3 {
    color: #814156;
  }
  .theme-desc {
    color: #814156;
  }
  .theme-btn {
    background-color: #A44665;
  }
  .theme-radio {
    color: #814156;
    .radio.selected {
      border-color: #A44665;
      span {
        background-color: #A44665;
      }
    }
  }
  .theme-balance {
    color: #BF7B90;
  }
  .theme-sub-btn {
    background: #FFF9F8;
    color: #A44665;
  }
  .theme-save-btn {
    background: #FFF9F8;
    color: #A44665;
  }
  .introduce {
    background-color: #FFF9F8;
    color: #A44665;
    .bg-icon {
      background: url('@/assets/introduce/red.png') no-repeat center;
      background-size: contain;
    }
    .divice-line {
      background-image: linear-gradient(to right, #F8D2CB 0%, #F8D2CB 50%, transparent 50%);
    }

  }
  .chapter-title,.chapter-content{
    color: #814156;
  }
}

// 黄色
.color-FBE6B5 {
  .mask {
    background-image: linear-gradient(180deg, rgba(251,230,181,0.00) 20%, #FBE6B5 100%);
  }
  .theme-h3 {
    color: #6A482E;
  }
  .theme-desc {
    color: #6A482E;
  }
  .theme-btn {
    background-color: #C16F30;
  }
  .theme-radio {
    color: #6A482E;
    .radio.selected {
      border-color: #C16F30;
      span {
        background-color: #C16F30;
      }
    }
  }
  .theme-balance {
    color: #D1986C;
  }
  .theme-sub-btn {
    background: #FFFCF6;
    color: #C16F30;
  }
  .theme-save-btn {
    background: #FFFCF6;
    color: #C16F30;
  }
  .introduce {
    background-color: #FFFCF6;
    color: #C16F30;
    .bg-icon {
      background: url('@/assets/introduce/yellow.png') no-repeat center;
      background-size: contain;
    }
    .divice-line {
      background-image: linear-gradient(to right, #FBE6B5 0%, #FBE6B5 50%, transparent 50%);

    }

  }
  .chapter-title,.chapter-content{
    color: #6A482E;
  }
}
// 深色
.color-292929 {
  * {
    color: #aaa;
  }
  .mask {
    background-image: linear-gradient(180deg, rgba(41,41,41,0.00) 20%, #292929 100%);
  }
  .theme-h3 {
    color: #aaa;
  }
  .theme-desc {
    color: #aaa;
  }
  .theme-btn {
    span {
      color: #fff;
    }
  }
  .theme-radio {
    color: #aaa;
    .radio.selected {
      border-color: #aaa;
      span {
        background-color: #aaa;
      }
    }
  }
  .theme-balance {
    color: #636363;
  }
  .theme-btn-bg {
    background: #313030;
  }
  .theme-sub-btn {
    background: #585858;
    color: #D7D7D7;
  }
  .theme-save-btn {
    background: #585858;
    color: #D7D7D7;
  }
  .introduce {
    background-color: #434343;
    color: #D7D7D7;
    .bg-icon {
      background: url('@/assets/introduce/black.png') no-repeat center;
      background-size: contain;
    }
    .divice-line {
      background-image: linear-gradient(to right, #6D6666 0%, #6D6666 50%, transparent 50%);
    }

  }
  .chapter-title,.chapter-content{
    color: #AAAAAA;
  }
}
</style>
